import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useShopDetails } from '../../hooks/use-shop';
import { useSearch } from '../../hooks/use-search';
import { GET_REPORT } from '../../gql/queries';
import { Report } from '../../gql/graphql';
import Loader from '../../components/generic/Loader';
import { GenericModalV2 } from '../../components/generic/generic.modal';
import { Column, GenericTable } from '../../components/generic/generic.table';
import { CSVExporter } from '../../utils/csv-export.util';
import { ReportView } from '../../components/report/report-view';
import { inr } from '../../components/report/report-theme';
import { toReportModel } from '../../components/report/report-data';

const orderColumns: Column[] = [
	{ title: 'Order Id', key: 'sequenceNumber' },
	{ title: 'Customer', key: 'customerName' },
	{ title: 'Total', key: 'total', formatter: (_column: Column, data: any) => inr(data.total) },
	{ title: 'Payment Method', key: 'paymentMethod' },
	{
		title: 'Created',
		key: 'createdAt',
		formatter: (_column: Column, data: any) => new Date(data.createdAt).toLocaleDateString('en-IN'),
	},
];

const ReportsPage = () => {
	const { shopId, locationId, shopName } = useShopDetails();
	const { SearchComponent, searchQuery } = useSearch();
	const [showModal, setShowModal] = useState(false);

	const { data, loading, previousData } = useQuery<Record<'report', Report>>(GET_REPORT, {
		variables: {
			query: {
				shopId,
				locationId,
				startDate: searchQuery.from,
				endDate: searchQuery.to,
				stylistUserId: searchQuery.userId,
				timezone: searchQuery.timezone,
			},
		},
	});

	/* Hold the previous slice while refetching so the page never collapses to skeletons. */
	const raw = (data ?? previousData)?.report;
	const report = useMemo(() => toReportModel(raw), [raw]);

	const dateRange = `${new Date(searchQuery.from).toLocaleDateString()}_to_${new Date(
		searchQuery.to
	).toLocaleDateString()}`.replace(/\//g, '-');

	return (
		<>
			<Loader isLoading={loading && !previousData} />
			<ReportView
				report={report}
				shopName={shopName}
				from={new Date(searchQuery.from)}
				to={new Date(searchQuery.to)}
				filterBar={SearchComponent(loading)}
				refreshing={loading && Boolean(previousData)}
				exportDisabled={!raw}
				onExport={() => CSVExporter.exportReportData(raw, shopName || 'Shop', dateRange)}
				onViewAllOrders={() => setShowModal(true)}
			/>

			{showModal && (
				<GenericModalV2 open={showModal} close={() => setShowModal(false)} title="All Orders">
					<GenericTable title="Orders" data={report.orders} columns={orderColumns} refetch={() => {}} />
				</GenericModalV2>
			)}
		</>
	);
};

export default ReportsPage;
